/**
 * Email Controller — Stimulus entry point for the 3-pane email UI (CSR mode).
 *
 * Responsibilities:
 *   - Reads bootstrap data from #email-app-root data attributes
 *   - Creates the emailStore (all signals live here)
 *   - Bootstraps the store with mailboxes/folders/tags/initial-folder from data attrs
 *   - Connects ActionCable (EmailJsonChannel) for live sidebar counts
 *   - Mounts <EmailApp /> into the actual #email-app-root div
 *   - Kicks off the first fetchEmails() so the list renders immediately
 *   - Initialises the layout manager (ResizeObserver, CSS var, signal effects)
 *   - Registers keyboard shortcuts (EmailKeyboardHandler on document)
 *   - Registers delegated drag-drop (EmailDragManager on this.element)
 *   - Cleans up all of the above on disconnect
 */

import { Controller } from '@hotwired/stimulus'
import { render, h } from 'preact'
import { I18n } from 'i18n-js'
import locales from '../locales.json'
import { createEmailStore } from '../modules/email/email-store.js'
import { EmailLayoutManager } from '../modules/email/email-layout-manager.js'
import { EmailKeyboardHandler } from '../modules/email/email-keyboard.js'
import { EmailDragManager } from '../modules/email/email-drag-manager.js'
import { EmailApp } from '../components/email/EmailApp.jsx'

export default class extends Controller {
  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'

    // Create the central store — all signals live here
    this.store = createEmailStore({ i18n: this.i18n })

    // Expose store globally so legacy sub-controllers / keyboard handler can reach it
    this.element._emailStore = this.store
    window._emailStore = this.store

    // Bootstrap from data attributes on the shell div
    const mailboxes    = this._parseDataAttr('emailMailboxes')
    const personalMailboxes = this._parseDataAttr('emailPersonalMailboxes')
    const folders      = this._parseDataAttr('emailFolders')
    const tags         = this._parseDataAttr('emailTags')
    const sidebarCounts = this._parseDataAttr('emailSidebarCounts')
    const initialFolder = this.element.dataset.emailInitialFolder || 'inbox'
    const personId     = this.element.dataset.emailPersonId

    this.store.bootstrap({
      mailboxesData:          mailboxes,
      personalMailboxesData:  personalMailboxes,
      emailFoldersData:       folders,
      emailTagsData:          tags,
      sidebarCountsData:      sidebarCounts,
      initialFolder,
    })

    // Mount <EmailApp /> directly into this.element (the #email-app-root div)
    render(h(EmailApp, { store: this.store, i18n: this.i18n }), this.element)

    // Connect ActionCable for live sidebar count updates
    if (personId) {
      this.store.connectActionCable(personId)
    }

    // Kick off initial email list fetch (and deep-link thread open when ?thread_id= is present)
    this.store.openInitialRouteFromUrl()

    // Layout: ResizeObserver, CSS vars, signal → CSS class effects
    this.layoutManager = new EmailLayoutManager({
      element: this.element,
      store: this.store,
    })
    this.layoutManager.connect()

    // Keyboard shortcuts: j/k/e/s/r/c//Esc
    this.keyboardHandler = new EmailKeyboardHandler({
      element: this.element,
      store: this.store,
    })
    this.keyboardHandler.connect()

    // Drag-drop: email rows → sidebar folder drop zones (event delegation)
    this.dragManager = new EmailDragManager({
      element: this.element,
    })
    this.dragManager.connect()
  }

  disconnect() {
    this.keyboardHandler?.disconnect()
    this.layoutManager?.disconnect()
    this.dragManager?.disconnect()

    // Disconnect ActionCable
    this.store?.disconnectActionCable()

    // Unmount Preact
    render(null, this.element)

    // Clean up global store reference
    if (window._emailStore === this.store) window._emailStore = null
    if (this.element._emailStore === this.store) this.element._emailStore = null
  }

  _parseDataAttr(attrName) {
    const raw = this.element.dataset[attrName]
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
}
